import { SymbolStore } from "./symbol-store"
import { ComputeEvaluator, toNum } from "./compute-evaluator"
import type { CacheStats } from "./expression-cache"
import type { CogoVariable } from "@cogo/types"

export class StateManager {
  private store: SymbolStore
  private compute: ComputeEvaluator

  constructor(variables: Record<string, CogoVariable>) {
    this.store = new SymbolStore()
    this.compute = new ComputeEvaluator(this)
    for (const [key, def] of Object.entries(variables)) {
      if (def.default !== undefined) {
        this.store.set(key, structuredClone(def.default))
      }
    }
  }

  get(path: string): unknown {
    return this.store.get(path)
  }

  setState(path: string, value: unknown): void {
    this.store.set(path, value)
  }

  /** Set state WITHOUT notifying listeners — prevents computed->setState infinite loops. */
  setStateQuiet(path: string, value: unknown): void {
    this.store.setQuiet(path, value)
  }

  appendTo(path: string, value: unknown): void {
    const arr = this.store.get(path)
    if (Array.isArray(arr)) {
      this.store.set(path, [...arr, value])
    }
  }

  removeWhere(path: string, predicate: (item: unknown) => boolean): void {
    const arr = this.store.get(path)
    if (Array.isArray(arr)) {
      this.store.set(path, arr.filter((item) => !predicate(item)))
    }
  }

  updateWhere(
    path: string,
    predicate: (item: unknown) => boolean,
    update: Record<string, unknown>,
  ): void {
    const arr = this.store.get(path)
    if (Array.isArray(arr)) {
      this.store.set(
        path,
        arr.map((item) =>
          predicate(item) ? { ...(item as object), ...update } : item,
        ),
      )
    }
  }

  /**
   * Upsert: if an item matching `matchExpr` exists, apply `onMatch`; otherwise append `onNew`.
   * matchExpr is an arrow function string, e.g., "item => item.id == 'prod_001'"
   * Uses ComputeEvaluator.evalPredicate() instead of new Function().
   */
  upsertWhere(
    path: string,
    matchExpr?: string,
    onMatch?: Record<string, unknown>,
    onNew?: Record<string, unknown>,
  ): void {
    const arr = this.store.get(path)
    if (!Array.isArray(arr)) {
      if (onNew) this.store.set(path, [onNew])
      return
    }

    const idx = matchExpr
      ? arr.findIndex((item) => this.compute.evalPredicate(matchExpr, item))
      : -1

    if (idx >= 0 && onMatch) {
      // Update existing item — resolve #{...} expressions with item context
      const updated = { ...(arr[idx] as object) } as Record<string, unknown>
      const itemObj = arr[idx] as Record<string, unknown>
      for (const [key, val] of Object.entries(onMatch)) {
        if (typeof val === "string" && val.includes("#{")) {
          const resolved = val.replace(/#\{([^}]+)\}/g, (_, expr) => {
            const r = (expr as string).trim()
            // Simple item property path: "item.quantity" (no operators)
            if (/^item\.\w+$/.test(r)) {
              const propPath = r.substring(5)
              const v = getNestedValue(itemObj, propPath)
              return String(v ?? "")
            }
            // Compound expression: "item.quantity + detail.quantity"
            const evaluated = this.resolveOnMatchExpr(r, itemObj)
            return String(evaluated ?? "")
          })
          const numVal = Number(resolved)
          updated[key] = !isNaN(numVal) && resolved.trim() !== "" ? numVal : resolved
        } else {
          updated[key] = val
        }
      }
      const newArr = [...arr]
      newArr[idx] = updated
      this.store.set(path, newArr)
    } else if (onNew) {
      this.store.set(path, [...arr, onNew])
    }
  }

  /** Resolve an onMatch expression like "item.quantity + detail.quantity". */
  private resolveOnMatchExpr(expr: string, item: Record<string, unknown>): unknown {
    // Replace item.xxx references with actual values
    let processed = expr.replace(/\bitem\.(\w+(?:\.\w+)*)/g, (_, field) => {
      const val = getNestedValue(item, field)
      if (typeof val === "number") return val.toString()
      if (typeof val === "string") {
        const n = parseFloat(val)
        return isNaN(n) ? JSON.stringify(val) : n.toString()
      }
      return String(val ?? "null")
    })

    // Replace remaining dot-path references with state values (e.g., detail.quantity)
    processed = processed.replace(/\b([a-zA-Z_]\w*(?:\.\w+)+)/g, (match) => {
      // Don't replace if it's already a number
      if (/^\d/.test(match)) return match
      const val = this.store.get(match)
      if (val !== undefined) {
        if (typeof val === "number") return val.toString()
        if (typeof val === "string") {
          const n = parseFloat(val)
          return isNaN(n) ? JSON.stringify(val) : n.toString()
        }
        return String(val)
      }
      return match
    })

    // If it's a simple number, return it
    const trimmed = processed.trim()
    const asNum = parseFloat(trimmed)
    if (!isNaN(asNum) && /^-?\d+(\.\d+)?$/.test(trimmed)) return asNum

    // Try evaluating as arithmetic
    try {
      return toNum(this.compute.evaluate(processed))
    } catch {
      return this.store.get(expr) ?? processed
    }
  }

  resolve(template: string): unknown {
    if (typeof template !== "string") return template

    const pureMatch = /^#\{([^}]+)\}$/.exec(template)
    if (pureMatch) {
      const expr = pureMatch[1].trim()
      // Simple path or negation → fast SymbolStore path
      if (/^!?[\w.]+$/.test(expr)) {
        const resolved = this.store.resolve(template)
        // If store returned the raw template unchanged, the symbol doesn't exist.
        // Return empty string instead of showing raw #{expression} in the UI.
        if (resolved === template) return ""
        return resolved
      }
      // Complex expression (ternary, ===, string literals) → ComputeEvaluator
      try {
        return this.compute.evaluate(expr)
      } catch {
        const resolved = this.store.resolve(template)
        if (resolved === template) return ""
        return resolved
      }
    }

    // Mixed interpolation: "text #{a} more #{b}"
    if (template.includes("#{")) {
      return template.replace(/#\{([^}]+)\}/g, (_, inner) => {
        const expr = (inner as string).trim()
        // Simple path or negation → fast SymbolStore lookup
        if (/^!?[\w.]+$/.test(expr)) {
          const path = expr.startsWith("!") ? expr.slice(1) : expr
          const val = this.store.get(path)
          if (expr.startsWith("!")) return String(!val)
          return val != null ? String(val) : ""
        }
        // Complex expression → ComputeEvaluator
        try {
          const result = this.compute.evaluate(expr)
          return result != null ? String(result) : ""
        } catch {
          const val = this.store.get(expr)
          return val != null ? String(val) : ""
        }
      })
    }

    return template
  }

  subscribe(path: string, listener: (value: unknown) => void): () => void {
    return this.store.subscribe(path, listener)
  }

  getSymbolStore(): SymbolStore {
    return this.store
  }

  getCompute(): ComputeEvaluator {
    return this.compute
  }

  /** Return expression cache performance statistics. */
  getCacheStats(): CacheStats {
    return this.compute.getCacheStats()
  }

  /** Clear all cached expression classifications. */
  clearExpressionCache(): void {
    this.compute.clearCache()
  }
}

function getNestedValue(obj: unknown, path: string): unknown {
  const keys = path.split(".")
  let current: unknown = obj
  for (const key of keys) {
    if (current == null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}
